import { useCallback, useEffect, useRef } from 'react';
import { useChartContext } from '../components/chart.context';
import { ISeriesPrimitive, Time } from 'lightweight-charts';
import { Marker } from '../primitives/marker.primitive';
import { Trade } from '@server/data-feed/schemas/output.schema';

const markerConfig = {
  takeProfit: { color: '#2c9f20' },
  stopLoss: { color: '#f348a3' },
  exit: { color: 'yellow' }
};

export const useCustomMarkers = () => {
  const { chartRef, candlestickSeriesRef, chartInitialized, data } =
    useChartContext();

  const { takeProfits, stopLosses, exits } = data.execution;
  const executionRef = useRef({ takeProfits, stopLosses, exits });
  const markerRefs = useRef<ISeriesPrimitive<Time>[]>([]);

  const clearExistingPrimitives = useCallback(() => {
    if (!candlestickSeriesRef.current) return;

    markerRefs.current.forEach((primitive) => {
      candlestickSeriesRef.current?.detachPrimitive(primitive);
    });
    markerRefs.current = [];
  }, [candlestickSeriesRef]);

  const createMarker = useCallback(
    (markerData: Trade, config: typeof markerConfig.takeProfit) => {
      if (!markerData.exitPrice) {
        console.warn(
          'Marker creation skipped - missing exit price',
          markerData
        );
        return null;
      }

      if (!chartRef.current || !candlestickSeriesRef.current) return;

      return new Marker(
        chartRef.current,
        candlestickSeriesRef.current,
        markerData.time,
        markerData.exitPrice,
        config
      );
    },
    []
  );

  const addMarkers = useCallback(
    (data: Trade[], config: typeof markerConfig.takeProfit) => {
      return data
        .map((item) => createMarker(item, config))
        .filter(Boolean) as ISeriesPrimitive<Time>[];
    },
    [createMarker]
  );

  const initializeOrUpdate = useCallback(() => {
    const series = candlestickSeriesRef.current;

    if (!chartInitialized || !series) return;

    clearExistingPrimitives();

    const { takeProfits, stopLosses, exits } = executionRef.current;
    const newPrimitives = [
      ...addMarkers(takeProfits || [], markerConfig.takeProfit),
      ...addMarkers(stopLosses || [], markerConfig.stopLoss),
      ...addMarkers(exits || [], markerConfig.exit)
    ];

    newPrimitives.forEach((primitive) => {
      series.attachPrimitive(primitive);
    });

    markerRefs.current = newPrimitives;
  }, [
    chartRef,
    candlestickSeriesRef,
    clearExistingPrimitives,
    addMarkers,
    chartInitialized
  ]);

  useEffect(() => {
    executionRef.current = { takeProfits, stopLosses, exits };
    initializeOrUpdate();
  }, [takeProfits, stopLosses, exits, initializeOrUpdate]);

  useEffect(() => {
    return () => {
      clearExistingPrimitives();
    };
  }, [clearExistingPrimitives]);

  return {
    initializeOrUpdate
  };
};
